import { useMemo } from 'react';

import { useMutation, UseMutationResult, useQueryClient } from 'react-query';

import { useSnackbar } from 'src/utils/hooks';

import { FILE_STATUS, QUERY_NAME } from '../constants';
import {
  ChangeStatusPageFileVariables,
  ContentHubFileSchema,
} from '../typings';
import { requestReviewContentHubPageFile } from '../requests/requestReviewContentHubPageFile';

interface UseMutationContext {
  previousData?: ContentHubFileSchema[];
}

export type UseReviewContentHubPageFileResult = UseMutationResult<
  void,
  Error,
  ChangeStatusPageFileVariables,
  UseMutationContext
>;

export const useReviewContentHubPageFile = (
  pageId: number
): UseReviewContentHubPageFileResult => {
  const queryClient = useQueryClient();

  const { enqueueErrorSnackbar, enqueueSuccessSnackbar } = useSnackbar();

  const queryKey = useMemo(() => {
    return pageId
      ? [QUERY_NAME.fetchContentHubPageFiles, pageId]
      : [QUERY_NAME.fetchContentHubPageFiles];
  }, [pageId]);

  const mutation = useMutation<
    void,
    Error,
    ChangeStatusPageFileVariables,
    UseMutationContext
  >((variables) => requestReviewContentHubPageFile(pageId, variables), {
    onMutate: async () => {
      await queryClient.cancelQueries(queryKey);

      const previousData =
        queryClient.getQueryData<ContentHubFileSchema[]>(queryKey);

      return { previousData };
    },

    onSuccess: (_data, { id }, context) => {
      const reviewedFile = context?.previousData?.find(
        (file) => file.id === id
      );

      queryClient.setQueryData<ContentHubFileSchema[]>(
        queryKey,
        (previous = []) =>
          previous.map((file) => {
            if (file.id === id) {
              return { ...file, status: FILE_STATUS.pending };
            }

            return file;
          })
      );

      if (reviewedFile) {
        enqueueSuccessSnackbar({
          message: 'page.content_management.api.files.review',
          intlValues: {
            name: reviewedFile.name,
            extension: reviewedFile.extension,
          },
        });
      }
    },

    onError: ({ message }, _variables, context) => {
      queryClient.setQueryData(queryKey, context?.previousData);

      enqueueErrorSnackbar({ message });
    },
  });

  return mutation;
};
